/** @jsxImportSource theme-ui */
import React from 'react';
import { useSelector } from 'react-redux';
import { SxProp } from 'theme-ui';

import { InsightsState } from 'app-redux/InsightsState';
import { RiskFeedEntryModel } from 'client/dto';
import { RiskLikelihood } from 'models/RiskCategories';
import { SpeciesIdHuman } from 'utils/constants';
import { getRiskLikelihood } from 'utils/modelHelpers';

import { BdIcon } from 'components/_common/BdIcon';
import { Typography } from 'components/_common/Typography';
import BdPrimaryLink from 'components/_controls/BdLink/BdPrimaryLink';

import diseasesSelectors from 'diseases/diseases.selectors';
import locationsSelectors from 'locations/locations.selectors';
import riskOverviewSelectors from 'riskOverview/riskOverview.selectors';

import { summaryPanelStyle } from '../RiskOverviewStyles';
import CaseCountSummary from './CaseCountSummary';
import ImportationRiskLikelihoodSummary from './ImportationRiskLikelihoodSummary';
import LocationVulnerabilitySummary from './LocationVulnerabilitySummary';
import NoChanceOfCasesSummary from './NoChanceOfCasesSummary';
import PreventabilitySummary from './PreventabilitySummary';
import SeveritySummary from './SeveritySummary';
import TransmissibilitySummary from './TransmissibilitySummary';

type RiskOverviewSummaryProps = SxProp & {
  locationId: number;
  diseaseActivityId: number;
};

const RiskOverviewSummary: React.FC<RiskOverviewSummaryProps> = ({
  locationId,
  diseaseActivityId,
  ...props
}) => {
  const riskFeedEntry: RiskFeedEntryModel = useSelector((state: InsightsState) =>
    riskOverviewSelectors.getRiskFeedEntryByActivityIdAndLocationId(state)
  )(diseaseActivityId, locationId);
  const disruption = useSelector(
    riskOverviewSelectors.getRiskFeedEntryDisruptionFactors(diseaseActivityId, locationId)
  );
  const diseaseName: string = useSelector<InsightsState, string>(
    diseasesSelectors.getDiseaseName(riskFeedEntry?.diseaseId)
  );
  const locationName: string = useSelector<InsightsState, string>(
    locationsSelectors.getLocationName(riskFeedEntry?.locationId)
  );
  const importationRiskLikelihood: RiskLikelihood = getRiskLikelihood(
    riskFeedEntry?.importationRisk
  );
  const isDetailsLinkEnabled =
    riskFeedEntry?.reportedCases > 0 || riskFeedEntry?.riskPriorityScore > 0.001;

  const summaryPoints = [
    CaseCountSummary({
      reportedCases: riskFeedEntry?.reportedCases,
      parentLocationReportedCases: riskFeedEntry?.caseIncidence?.parentLocationReportedCases,
      locationName,
      isInherited: riskFeedEntry?.caseIncidence?.isInherited,
      parentLocationName: riskFeedEntry?.caseIncidence?.parentLocationName,
      diseaseName,
    }),
    ImportationRiskLikelihoodSummary({
      locationName,
      importationRiskLikelihood: importationRiskLikelihood,
    }),
    NoChanceOfCasesSummary({
      reportedCases: riskFeedEntry.reportedCases,
      parentLocationReportedCases: riskFeedEntry?.caseIncidence?.parentLocationReportedCases,
      locationName,
      parentLocationName: riskFeedEntry?.caseIncidence?.parentLocationName,
      importationRiskLikelihood: importationRiskLikelihood,
    }),
    SeveritySummary({
      severity: disruption?.severity,
      diseaseName,
    }),
    PreventabilitySummary({
      preventability: disruption?.preventability,
    }),
    TransmissibilitySummary({
      transmissibility: disruption?.transmissibility,
      diseaseName,
      locationName,
      parentLocationName: riskFeedEntry?.distributedCasesLocationName,
    }),
    LocationVulnerabilitySummary({
      locationResiliency: disruption?.locationResiliency,
      locationName,
    }),
  ];

  return (
    <div
      data-testid={`overview-summary-${locationId}-${diseaseActivityId}`}
      sx={summaryPanelStyle}
      {...props}
    >
      <div className="ro-summary-points" sx={{ mb: '24px' }}>
        {summaryPoints.map((summary, index) => {
          return (
            summary && (
              <Typography
                key={index}
                variant="body2"
                color="stone90"
                marginBottom="12px"
                sx={{ display: 'flex' }}
              >
                <div sx={{ pt: '8px', pr: '8px' }}>
                  <div
                    sx={{
                      width: '4px',
                      height: '4px',
                      borderRadius: '50%',
                      backgroundColor: 'stone90',
                    }}
                  />
                </div>
                {summary}
              </Typography>
            )
          );
        })}
      </div>
      {isDetailsLinkEnabled ? (
        <BdPrimaryLink
          data-html2canvas-ignore="true"
          variant="body1"
          href={`/location/${locationId}/disease/${riskFeedEntry.diseaseId}/species/${SpeciesIdHuman}`}
        >
          See details{' '}
          <BdIcon
            name="icon-chevron-right"
            sx={{ '&.icon.bd-icon': { margin: '0', fontWeight: 'bold', fontSize: '12px' } }}
          />
        </BdPrimaryLink>
      ) : (
        <Typography data-html2canvas-ignore="true" variant="body1" color="stone50">
          There are no additional details to see
        </Typography>
      )}
    </div>
  );
};

export default RiskOverviewSummary;
